// <product-switcher> — PDP switcher between sibling products along one axis.
//
// Some choices aren't variants of a product but separate products: pillowcase
// size (a product per "Standard Pillowcase" / "European Pillowcase" / …), duvet
// insert warmth (a product per "Winter Warmer" / "Cool Comfort" / …). This
// element offers a button per sibling and swaps the product section in place
// (via window.swapProductSection, shared with the colour swatch links) rather
// than navigating, so scroll position survives the switch.
//
// WHICH products are siblings and WHAT the options are called isn't hardcoded
// here — it comes from the "Product switcher" metaobject, one entry per axis,
// which product-switcher.liquid resolves by product type and passes down as data-*:
//
//   data-types        JSON array — the product types in this group. Siblings are
//                     the products of those types; for pillowcases the type IS
//                     the size, so the group spans all five of them.
//   data-options      JSON array — the option labels in display order, and the
//                     allow-list: only these are offered. A key matching one
//                     (case-insensitively) takes its exact text; anything else
//                     is dropped, and a product whose OWN key isn't listed gets
//                     no switcher at all — so a new size stays hidden until the
//                     metaobject names it.
//   data-option-name  what's being picked ("Size", "Style") — the accessible
//                     label for the row.
//
// Two of the current product's own attributes decide how siblings are matched
// and keyed, and both are emitted only when they apply:
//
//   data-fabric       siblings must share this fabric and colour. Set only for
//   data-colour       products that have them (pillowcases do, inserts don't):
//                     it's what preserves the colour across a switch, and what
//                     keeps the scalloped line separate from the plain one —
//                     their colour names differ ("Terracotta" vs "Terracotta &
//                     Rust Scalloped").
//   data-current-size set when "Size" is single-valued, i.e. size is what
//                     distinguishes this product from its siblings rather than
//                     one of its own variants. Its presence picks the axis: the
//                     size when set, otherwise the title with the product type
//                     stripped off ("Winter Warmer Duvet Insert" -> "Winter
//                     Warmer").

const STOREFRONT_API_VERSION = '2023-04';

// One page covers any plausible group — the largest today (every pillowcase, all
// five types across every colour) is ~100 products. A group that outgrew this
// would silently lose its tail, so it's deliberately far above the need.
const SWITCHER_QUERY = /* GraphQL */ `
	query productSwitcher($q: String!) {
		products(first: 250, query: $q) {
			nodes {
				handle
				title
				id
				productType
				featuredImage { url }
				fabric: metafield(namespace: "custom", key: "fabric") {
					reference { ... on Metaobject { handle } }
				}
				colour: metafield(namespace: "custom", key: "colour") { value }
				options { name values }
			}
		}
	}`;

// Storefront lookups are cached across re-inits (each section swap re-creates
// this element), keyed by the group + colourway they're looking up.
const fetchCache = new Map();

function parseList(value) {
	try {
		const list = JSON.parse(value || '[]');
		if (!Array.isArray(list)) return [];
		return list.filter(v => typeof v === 'string' && v.trim()).map(v => v.trim());
	} catch (e) {
		return [];
	}
}

function toRecord(node) {
	if (!node || !node.handle) return null;
	return {
		u: `/products/${node.handle}`,
		t: node.title || '',
		i: node.featuredImage?.url || '',
		p: node.id ? String(node.id).split('/').pop() : '',
	};
}

// "Standard Size" -> "Standard"; leaves anything without a trailing "Size" as-is.
// Pillowcases carry the suffix, inserts don't, and dropping it lets one set of
// options serve both.
function sizeLabel(value) {
	return (value || '').replace(/\s*Size$/i, '').trim() || (value || '').trim();
}

// "Winter Warmer Duvet Insert" + "Duvet Insert" -> "Winter Warmer". Falls back to
// the untouched title when the type isn't a suffix of it, or is the whole of it
// (a "Cushion Insert" of type "Cushion Insert").
function stripType(title, type) {
	const name = (title || '').trim();
	if (!type || !name.endsWith(type)) return name;
	return name.slice(0, -type.length).trim() || name;
}

class ProductSwitcher extends HTMLElement {
	connectedCallback() {
		if (this._initialised) return;
		this._initialised = true;

		this.sectionId = document.getElementById('ProductSection-product-template')?.dataset.sectionId;

		this.types = parseList(this.dataset.types);
		this.options = parseList(this.dataset.options);
		this.fabric = (this.dataset.fabric || '').trim();
		this.colour = (this.dataset.colour || '').trim();

		// Non-empty in size mode, blank in title mode — see the header.
		this.size = sizeLabel(this.dataset.currentSize);
		this.key = this.canonicalise(this.size || stripType(this.dataset.currentTitle, this.dataset.currentType));

		// The element ships with a server-rendered skeleton (see the snippet) so a
		// placeholder is painted on initial page load, before this deferred module
		// runs. render() replaces it with the real buttons, or collapse() clears it.
		//
		// `options` is the allow-list, so a product whose own key it doesn't name
		// has no row to belong to — that covers an empty list too. canonicalise()
		// has already normalised the key's spelling to the metaobject's.
		if (!this.types.length || !this.key || !this.options.includes(this.key)) return this.collapse();

		this.render();
	}

	// The metaobject's spelling of a key, so its `options` order applies and its
	// casing wins ("Does-it-All" vs a US title's "Does-It-All").
	canonicalise(key) {
		if (!key) return '';
		return this.options.find(option => option.toLowerCase() === key.toLowerCase()) || key;
	}

	// The key a product represents: its size where size is the axis, otherwise its
	// name. Returns '' for a product that can't take part — in size mode, one
	// whose Size is a variant axis rather than an identity.
	keyFor(node) {
		if (!this.size) return stripType(node.title, node.productType);
		const values = node.options?.find(option => option.name === 'Size')?.values || [];
		return values.length === 1 ? sizeLabel(values[0]) : '';
	}

	fetchEntries() {
		const cacheKey = `${this.types.join('|')}::${this.fabric}::${this.colour}`;
		if (fetchCache.has(cacheKey)) return fetchCache.get(cacheKey);

		const q = this.types.map(type => `product_type:'${type.replace(/'/g, "\\'")}'`).join(' OR ');

		const promise = fetch(`/api/${STOREFRONT_API_VERSION}/graphql.json`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-Shopify-Storefront-Access-Token': theme.storefrontToken,
			},
			body: JSON.stringify({ query: SWITCHER_QUERY, variables: { q } }),
		})
			.then(resp => {
				if (!resp.ok) throw new Error(`${this.tagName} lookup failed: ${resp.status}`);
				return resp.json();
			})
			.then(json => this.parseNodes(json?.data?.products?.nodes || []));

		fetchCache.set(cacheKey, promise);
		return promise;
	}

	parseNodes(nodes) {
		const entries = new Map(); // key -> { label, record }

		for (const node of nodes) {
			// product_type: is a fuzzy match, so pin it to the group — without this a
			// "Pillow Insert" lookup would also pull in "Cushion Insert" products.
			if (!this.types.includes(node.productType)) continue;
			if (this.fabric && node.fabric?.reference?.handle !== this.fabric) continue;
			if (this.colour && (node.colour?.value || '').trim() !== this.colour) continue;

			const key = this.canonicalise(this.keyFor(node));
			if (!key || !this.options.includes(key) || entries.has(key)) continue;

			entries.set(key, { label: key, record: toRecord(node) });
		}

		// Unordered here — every surviving key is in `options`, so render() sorts
		// them by it.
		return entries;
	}

	// Builds the axis label ("Size: Standard"). Visually hidden — see CSS.
	makeLabel(currentText) {
		const labelEl = document.createElement('span');
		labelEl.className = 'product-switcher__label';
		labelEl.append(`${this.dataset.optionName || ''}: `);
		const current = document.createElement('span');
		current.className = 'product-switcher__current';
		current.textContent = currentText;
		labelEl.appendChild(current);
		return labelEl;
	}

	// Removes the switcher (and any skeleton) from the page.
	collapse() {
		this.replaceChildren();
		this.hidden = true;
	}

	async render() {
		let entries;
		try {
			entries = await this.fetchEntries();
		} catch (e) {
			return this.collapse(); // fail silently — the PDP is usable without the switcher
		}

		// Ensure the current option is represented even if the query missed it.
		if (!entries.has(this.key)) entries.set(this.key, { label: this.key });

		// Only show the switcher when there's actually another option to pick.
		if (entries.size < 2) return this.collapse();

		const ordered = [...entries.entries()].sort((a, b) => this.options.indexOf(a[0]) - this.options.indexOf(b[0]));

		const prefetchUrls = [];
		const list = this.buildButtons(ordered, prefetchUrls);
		const currentLabel = entries.get(this.key)?.label || this.key;

		this.replaceChildren(this.makeLabel(currentLabel), list);

		this.hidden = false;

		// Warm the target sections while the browser is idle so the first switch
		// is instant, even on touch devices where there's no hover to prefetch on.
		if (window.prefetchProductSection && this.sectionId && prefetchUrls.length) {
			const warm = () => prefetchUrls.forEach(url => window.prefetchProductSection(url, this.sectionId));
			if (window.requestIdleCallback) requestIdleCallback(warm, { timeout: 3000 });
			else setTimeout(warm, 1500);
		}
	}

	// Builds the <ul> of options: a link per key that switches product, and a
	// plain (inert) button for the one already showing. Collects the link targets
	// into prefetchUrls so the caller can warm them.
	buildButtons(ordered, prefetchUrls) {
		const list = document.createElement('ul');
		list.className = 'product-switcher__buttons';

		for (const [key, entry] of ordered) {
			const item = document.createElement('li');
			item.className = 'product-switcher__button';

			if (key === this.key) {
				const btn = document.createElement('button');
				btn.type = 'button';
				btn.setAttribute('aria-current', 'true');
				btn.setAttribute('data-active', '');
				btn.textContent = entry.label;
				item.appendChild(btn);
				list.appendChild(item);
				continue;
			}

			const record = entry.record;
			if (!record || !record.u) continue;

			prefetchUrls.push(record.u);

			const link = document.createElement('a');
			link.href = record.u;
			link.textContent = entry.label;

			const prefetch = () => window.prefetchProductSection && window.prefetchProductSection(record.u, this.sectionId);
			link.addEventListener('mouseenter', prefetch);
			link.addEventListener('mousedown', prefetch);
			link.addEventListener('touchstart', prefetch, { passive: true });

			link.addEventListener('click', e => {
				if (!window.swapProductSection || !this.sectionId) return; // let the browser navigate
				e.preventDefault();
				window.swapProductSection({
					link: record.u,
					sectionId: this.sectionId,
					title: record.t,
					image: record.i,
					productId: record.p,
				});
			});

			item.appendChild(link);
			list.appendChild(item);
		}

		return list;
	}
}

if (!customElements.get('product-switcher')) {
	customElements.define('product-switcher', ProductSwitcher);
}
